import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { TrendingUp, TrendingDown, Plus } from 'lucide-react';

const usd = (n) => `$${(Number(n) || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;

/**
 * Quick add-entry form for the ledger tab. Pure UI — parent owns state + submit handler.
 *
 * Props:
 *  - form: { date, type, category, amount, note, estimate_id, customer_email }
 *  - setForm: state setter (used directly for simple field updates)
 *  - catList: list of category strings to populate the Select
 *  - estimates: array of estimate docs for the optional "link to estimate" picker
 *  - onChangeType(newType): switches income/expense + resets category
 *  - onSelectEstimate(id): autofills amount + customer_email + note
 *  - onSubmit(ev): form submit handler
 *  - saving: boolean disables submit button while in-flight
 */
const LedgerAddForm = ({ form, setForm, catList, estimates, onChangeType, onSelectEstimate, onSubmit, saving }) => (
  <Card className="bg-slate-900 border-slate-800">
    <CardHeader className="pb-3">
      <CardTitle className="text-base text-white flex items-center gap-2">
        <Plus className="w-4 h-4 text-red-400" /> Quick add entry
      </CardTitle>
    </CardHeader>
    <CardContent>
      <form onSubmit={onSubmit} className="space-y-3" data-testid="ledger-add-form">
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="ledger-form-type-income"
            onClick={() => onChangeType('income')}
            className={`flex-1 px-3 py-2 rounded text-sm border transition ${
              form.type === 'income'
                ? 'bg-green-600/20 border-green-500 text-green-300'
                : 'bg-slate-800 border-slate-700 text-gray-400 hover:bg-slate-700'
            }`}
          >
            <TrendingUp className="w-3 h-3 inline mr-1" /> Income
          </button>
          <button
            type="button"
            data-testid="ledger-form-type-expense"
            onClick={() => onChangeType('expense')}
            className={`flex-1 px-3 py-2 rounded text-sm border transition ${
              form.type === 'expense'
                ? 'bg-red-600/20 border-red-500 text-red-300'
                : 'bg-slate-800 border-slate-700 text-gray-400 hover:bg-slate-700'
            }`}
          >
            <TrendingDown className="w-3 h-3 inline mr-1" /> Expense
          </button>
        </div>
        <div>
          <Label className="text-gray-300 text-xs">Date</Label>
          <Input
            type="date"
            data-testid="ledger-form-date"
            value={form.date}
            onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
            required
            className="bg-slate-800 border-slate-700 text-white"
          />
        </div>
        <div>
          <Label className="text-gray-300 text-xs">Category</Label>
          <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
            <SelectTrigger data-testid="ledger-form-category" className="bg-slate-800 border-slate-700 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700 text-white">
              {catList.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {form.type === 'income' && estimates.length > 0 && (
          <div>
            <Label className="text-gray-300 text-xs">Link to estimate <span className="text-gray-500">(optional)</span></Label>
            <Select value={form.estimate_id || 'none'} onValueChange={(v) => onSelectEstimate(v === 'none' ? '' : v)}>
              <SelectTrigger data-testid="ledger-form-estimate" className="bg-slate-800 border-slate-700 text-white">
                <SelectValue placeholder="No linked estimate" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700 text-white">
                <SelectItem value="none">— None —</SelectItem>
                {estimates.map((est) => (
                  <SelectItem key={est.id} value={est.id}>
                    {est.estimate_number} · {est.customer_name} · {usd(est.total)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div>
          <Label className="text-gray-300 text-xs">Amount ($)</Label>
          <Input
            type="number"
            step="0.01"
            min="0.01"
            data-testid="ledger-form-amount"
            value={form.amount}
            onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
            required
            placeholder="0.00"
            className="bg-slate-800 border-slate-700 text-white tabular-nums"
          />
        </div>
        <div>
          <Label className="text-gray-300 text-xs">Note (optional)</Label>
          <Textarea
            rows={2}
            data-testid="ledger-form-note"
            value={form.note}
            onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
            placeholder="Trigger job for John Doe…"
            className="bg-slate-800 border-slate-700 text-white text-sm"
          />
        </div>
        <Button
          type="submit"
          data-testid="ledger-form-submit"
          disabled={saving}
          className={`w-full ${form.type === 'income' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'} text-white`}
        >
          {saving ? 'Saving…' : `Log ${form.type}`}
        </Button>
      </form>
    </CardContent>
  </Card>
);

export default LedgerAddForm;
